import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, FileCheck, Check } from "lucide-react";
import { formatINR } from "@/lib/format";

/**
 * Modal that offers two verification tiers after the owner fills the property form.
 * tiers[0] = Location & Owner Verification (standard, ~₹399)
 * tiers[1] = Document Verified (premium, ₹1,999)
 *
 * props:
 *   open, onOpenChange
 *   feeConfig: {listing_fee_paise, document_verified_fee_paise}
 *   onPick: ({tier, purpose, amount_paise}) => void
 */
export const VerificationTierPicker = ({ open, onOpenChange, feeConfig, onPick, busy = false }) => {
  const [selected, setSelected] = useState("standard");
  const stdPrice = feeConfig?.listing_fee_paise || 39900;
  const docPrice = feeConfig?.document_verified_fee_paise || 199900;

  const confirm = () => {
    if (selected === "standard") {
      onPick({ tier: "standard", purpose: "listing_fee", amount_paise: stdPrice });
    } else {
      onPick({ tier: "document_verified", purpose: "document_verified_fee", amount_paise: docPrice });
    }
  };

  const Tier = ({ id, icon: Icon, title, price, perks, highlight }) => {
    const isSelected = selected === id;
    return (
      <button
        type="button"
        onClick={() => setSelected(id)}
        data-testid={`tier-${id}`}
        className={`relative text-left p-5 rounded-2xl border-2 transition-all ${
          isSelected ? "border-[var(--primary)] bg-[rgba(200,90,50,0.04)]" : "border-[var(--line)] hover:border-[var(--line-strong)]"
        }`}
      >
        {highlight && (
          <span className="absolute -top-2 right-4 chip-gold chip text-[10px]">Recommended</span>
        )}
        <div className="flex items-start justify-between mb-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: isSelected ? "var(--primary)" : "var(--surface)", color: isSelected ? "#fff" : "var(--primary)" }}>
            <Icon className="w-5 h-5" />
          </div>
          {isSelected && (
            <span className="w-5 h-5 rounded-full bg-[var(--primary)] text-white flex items-center justify-center">
              <Check className="w-3 h-3" />
            </span>
          )}
        </div>
        <div className="font-serif text-lg text-[var(--teak)]">{title}</div>
        <div className="font-mono text-2xl font-bold text-[var(--primary)] mt-1">{formatINR(price / 100, "buy")}</div>
        <ul className="mt-3 space-y-1.5">
          {perks.map((p, i) => (
            <li key={i} className="text-xs text-[var(--ink-muted)] flex items-start gap-1.5">
              <Check className="w-3 h-3 text-[var(--emerald)] mt-0.5 shrink-0" /> {p}
            </li>
          ))}
        </ul>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="tier-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Choose your verification plan</DialogTitle>
          <p className="text-sm text-[var(--ink-muted)] mt-1">Pick the tier that fits your listing. You can upgrade later.</p>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <Tier
            id="standard"
            icon={ShieldCheck}
            title="Location & Owner Verification"
            price={stdPrice}
            perks={[
              "Address and GPS location checked manually",
              "Owner phone verified via OTP",
              "Property photos reviewed for authenticity",
              "Live within 24 hours of approval",
              "Standard 'Verified' badge on listing",
            ]}
          />
          <Tier
            id="document_verified"
            icon={FileCheck}
            title="Document Verified"
            price={docPrice}
            highlight
            perks={[
              "Everything in Location & Owner",
              "RERA, Khata, OC, Sale Deed vetted by our legal team",
              "Premium 'Document Verified' gold badge",
              "Priority placement on Buy/Rent pages",
              "Buyer document vault auto-unlocks after visit",
              "Dedicated support until a visit is booked",
            ]}
          />
        </div>
        <button
          onClick={confirm}
          disabled={busy}
          className="btn-primary w-full mt-4"
          data-testid="tier-pick-pay-btn"
        >
          {busy ? "Processing payment…" : `Pay ${formatINR((selected === "standard" ? stdPrice : docPrice) / 100, "buy")} & Submit`}
        </button>
        <p className="text-[10px] text-[var(--ink-muted)] text-center mt-1">Secured by Razorpay · Refundable within 24 hours if listing is rejected.</p>
      </DialogContent>
    </Dialog>
  );
};
